import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import Header from './Header';
import { isSupabaseConfigured, supabase } from '../lib/supabaseClient';
import '../Style/Blog.css';

const LEGACY_STORAGE_KEY = 'rin0700_blog_posts';
const DRAFT_STORAGE_KEY = 'rin0700_blog_drafts';

const samplePosts = [
  {
    id: 'welcome-rin0700',
    title: 'rin0700 blog start',
    summary: '制作、開発、メタバースの記録を残していくブログです。',
    category: 'Diary',
    categories: ['Diary'],
    thumbnailUrl: '/rin.png',
    createdAt: '2026-04-12',
    createdAtIso: '2026-04-12T00:00:00.000Z',
    displayDate: '2026-04-12 09:00',
    blocks: [
      {
        type: 'text',
        value:
          'ここでは制作メモ、技術メモ、メタバースで学んだことを記事としてまとめていきます。',
      },
      {
        type: 'link',
        label: 'X / @rin_0700',
        url: 'https://x.com/rin_0700',
      },
    ],
  },
];

const TEXT_FORMAT_OPTIONS = [
  { value: 'paragraph', label: '本文' },
  { value: 'h2', label: '見出し' },
  { value: 'bold', label: '強調リード' },
  { value: 'underline', label: '下線' },
  { value: 'dashed', label: '破線下線' },
  { value: 'small', label: '注釈' },
  { value: 'large', label: '大きめテキスト' },
  { value: 'quote', label: '引用' },
];

const BLOCK_TYPE_OPTIONS = [
  {
    type: 'text',
    label: 'テキスト',
    description: '本文、見出し、引用を追加します',
  },
  {
    type: 'image',
    label: '画像',
    description: '画像URLの入力または画像アップロード',
  },
  {
    type: 'link',
    label: 'リンク',
    description: 'テキストリンクやカードリンクを追加します',
  },
  {
    type: 'embed',
    label: '埋め込み',
    description: 'YouTube などの埋め込みリンクを追加します',
  },
  {
    type: 'file',
    label: 'ファイル',
    description: '配布ファイルや添付資料を追加します',
  },
  {
    type: 'quote',
    label: '引用',
    description: '引用文と出典を独立ブロックで追加します',
  },
  {
    type: 'code',
    label: 'コード',
    description: 'コードスニペットを整形して追加します',
  },
];

function createBlock(type) {
  if (type === 'link') {
    return {
      type: 'link',
      label: '',
      url: '',
      description: '',
      image: '',
      displayStyle: 'text',
    };
  }

  if (type === 'embed') {
    return {
      type: 'embed',
      url: '',
      title: '',
      caption: '',
    };
  }

  if (type === 'file') {
    return {
      type: 'file',
      label: '',
      url: '',
      fileName: '',
      fileSize: '',
      mimeType: '',
    };
  }

  if (type === 'quote') {
    return {
      type: 'quote',
      value: '',
      cite: '',
    };
  }

  if (type === 'code') {
    return {
      type: 'code',
      value: '',
      language: '',
      caption: '',
    };
  }

  return {
    type,
    value: '',
    format: type === 'text' ? 'paragraph' : undefined,
  };
}

function getBlockTitle(block) {
  if (block.type === 'image') return '画像';
  if (block.type === 'link') {
    return block.displayStyle === 'card' ? 'リンクカード' : 'リンク';
  }
  if (block.type === 'embed') return '埋め込み';
  if (block.type === 'file') return 'ファイル';
  if (block.type === 'quote') return '引用';
  if (block.type === 'code') return 'コード';

  if (block.format === 'h2') return '見出し';
  if (block.format === 'quote') return '引用';
  return 'テキスト';
}

const emptyDraft = {
  title: '',
  summary: '',
  category: '',
  thumbnailUrl: '',
  includeToc: false,
  blocks: [createBlock('text')],
};

const SITE_NAME = 'rin0700';
const DEFAULT_DESCRIPTION =
  'rin0700のポートフォリオと制作、開発、メタバースの記録。';
const DEFAULT_IMAGE_PATH = '/rin.png';
const THUMBNAIL_WIDTH = 1200;
const THUMBNAIL_HEIGHT = 675;
const BLOCKED_ATTACHMENT_EXTENSIONS = new Set([
  'bat',
  'cmd',
  'exe',
  'htm',
  'html',
  'jar',
  'js',
  'mjs',
  'php',
  'ps1',
  'sh',
  'svg',
  'xhtml',
  'xml',
]);

function formatDate(value) {
  if (!value) return '';
  return new Date(value).toISOString().slice(0, 10);
}

function formatDateTime(value) {
  if (!value) return '';

  const date = new Date(value);
  return new Intl.DateTimeFormat('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
    .format(date)
    .replace(/\//g, '-');
}

function getSiteOrigin() {
  return (
    process.env.REACT_APP_SITE_URL ||
    (typeof window !== 'undefined'
      ? window.location.origin
      : 'https://rin0700.com')
  ).replace(/\/$/, '');
}

function getAbsoluteUrl(path) {
  if (path?.startsWith('http')) return path;
  return `${getSiteOrigin()}${path || '/'}`;
}

function sanitizeUrl(value, options = {}) {
  const {
    allowRelative = false,
    allowHash = false,
    allowedProtocols = ['https:', 'http:'],
  } = options;
  const trimmedValue = String(value || '').trim();

  if (!trimmedValue) return '';
  if (trimmedValue.startsWith('selected:')) return trimmedValue;
  if (allowHash && trimmedValue.startsWith('#')) return trimmedValue;
  if (allowRelative && trimmedValue.startsWith('/')) return trimmedValue;

  try {
    const parsedUrl = new URL(trimmedValue);
    return allowedProtocols.includes(parsedUrl.protocol)
      ? parsedUrl.toString()
      : '';
  } catch {
    return '';
  }
}

function sanitizeImageSource(value) {
  return sanitizeUrl(value, { allowRelative: true });
}

function sanitizeLinkTarget(value) {
  return sanitizeUrl(value);
}

function sanitizeFileTarget(value) {
  return sanitizeUrl(value);
}

function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function setCanonical(url) {
  let element = document.head.querySelector('link[rel="canonical"]');

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }

  element.setAttribute('href', url);
}

function setStructuredData(data) {
  const scriptId = 'blog-structured-data';
  let element = document.getElementById(scriptId);

  if (!data) {
    element?.remove();
    return;
  }

  if (!element) {
    element = document.createElement('script');
    element.id = scriptId;
    element.type = 'application/ld+json';
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(data);
}

function getPostText(post) {
  return post.blocks
    .filter((block) => block.type === 'text' && block.value)
    .map((block) => block.value)
    .join(' ');
}

function getPostDescription(post) {
  const description = post.summary || getPostText(post) || DEFAULT_DESCRIPTION;
  return description.length > 140
    ? `${description.slice(0, 137).trim()}...`
    : description;
}

function getPostImage(post) {
  if (post.thumbnailUrl) {
    return getAbsoluteUrl(post.thumbnailUrl);
  }

  const imageBlock = post.blocks.find(
    (block) => block.type === 'image' && block.value
  );

  return getAbsoluteUrl(imageBlock?.value || DEFAULT_IMAGE_PATH);
}

function getPostThumbnail(post) {
  return getPostImage(post);
}

function getBlockImageSource(block, imagePreviews = {}) {
  if (block.value?.startsWith('selected:')) {
    return imagePreviews[block.value.replace('selected:', '')] || '';
  }

  return sanitizeImageSource(block.value);
}

function parseCategories(value) {
  return Array.from(
    new Set(
      String(value || '')
        .split(',')
        .map((category) => category.trim())
        .filter(Boolean)
    )
  );
}

function getPostCategories(post) {
  if (Array.isArray(post.categories) && post.categories.length > 0) {
    return post.categories;
  }

  return post.category ? [post.category] : ['Blog'];
}

function serializeCategories(categories) {
  return Array.from(new Set(categories.map((category) => category.trim())))
    .filter(Boolean)
    .join(', ');
}

function getTocItems(blocks) {
  return blocks
    .filter(
      (block) => block.type === 'text' && block.format === 'h2' && block.value
    )
    .map((block, index) => ({
      id: `toc-heading-${index}`,
      label: block.value,
    }));
}

function prepareBlocksForSave(draft) {
  const blocksWithoutToc = normalizeBlocks(draft.blocks).filter(
    (block) => block.type !== 'toc'
  );

  return draft.includeToc
    ? [{ type: 'toc', value: 'enabled' }, ...blocksWithoutToc]
    : blocksWithoutToc;
}

function mapDraftFromPost(post) {
  const blocks = (post.blocks || []).filter((block) => block.type !== 'toc');

  return {
    title: post.title || '',
    summary: post.summary || '',
    category: serializeCategories(getPostCategories(post)),
    thumbnailUrl: post.thumbnailUrl || '',
    includeToc: (post.blocks || []).some((block) => block.type === 'toc'),
    blocks: blocks.length ? blocks : [{ type: 'text', value: '' }],
  };
}

function mapPostFromSupabase(post) {
  const categories =
    Array.isArray(post.categories) && post.categories.length > 0
      ? post.categories
      : [post.category || 'Blog'];

  return {
    id: post.id,
    title: post.title,
    summary: post.summary || '',
    category: categories[0] || 'Blog',
    categories,
    thumbnailUrl: post.thumbnail_url || '',
    createdAt: formatDate(post.created_at),
    createdAtIso: post.created_at,
    displayDate: formatDateTime(post.created_at),
    blocks: Array.isArray(post.blocks) ? post.blocks : [],
  };
}

function readLegacyPosts() {
  try {
    const savedPosts = window.localStorage.getItem(LEGACY_STORAGE_KEY);
    return savedPosts ? JSON.parse(savedPosts) : [];
  } catch {
    return [];
  }
}

function readSavedDrafts() {
  try {
    const savedDrafts = window.localStorage.getItem(DRAFT_STORAGE_KEY);
    return savedDrafts ? JSON.parse(savedDrafts) : [];
  } catch {
    return [];
  }
}

function writeSavedDrafts(drafts) {
  window.localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(drafts));
}

function getDraftTitle(draft) {
  return draft.title?.trim() || 'Untitled draft';
}

function createDraftRecord(draft, id = crypto.randomUUID()) {
  const now = new Date().toISOString();

  return {
    id,
    title: getDraftTitle(draft),
    draft,
    updatedAt: now,
  };
}

function normalizeBlocks(blocks) {
  return (blocks || []).filter((block) => {
    if (block.type === 'toc') return Boolean(block.value);
    if (block.type === 'image') return Boolean(block.value);
    if (block.type === 'link') return Boolean(block.url || block.label);
    if (block.type === 'embed') return Boolean(block.url);
    if (block.type === 'file') {
      return Boolean(block.url || block.label || block.fileName);
    }
    if (block.type === 'quote' || block.type === 'code') {
      return Boolean(block.value);
    }

    return Boolean(block.value || block.url || block.label);
  });
}

function getFileExtension(file) {
  const extension = file.name.split('.').pop();
  return extension ? extension.toLowerCase() : 'jpg';
}

function createUploadToken(file) {
  const baseId =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.round(Math.random() * 100000)}`;

  return `${baseId}-${file.name}`;
}

function getSelectedUploadKey(value) {
  return value?.startsWith('selected:') ? value.replace('selected:', '') : '';
}

function formatFileSize(value) {
  const bytes = Number(value);
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getEmbedUrl(value) {
  if (!value) return '';

  try {
    const sanitizedUrl = sanitizeUrl(value);
    if (!sanitizedUrl) return '';

    const url = new URL(sanitizedUrl);
    const host = url.hostname.replace(/^www\./, '');

    if (host === 'youtu.be') {
      const videoId = url.pathname.split('/').filter(Boolean)[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
    }

    if (host === 'youtube.com' || host === 'm.youtube.com') {
      if (url.pathname === '/watch') {
        const videoId = url.searchParams.get('v');
        return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
      }

      if (url.pathname.startsWith('/shorts/')) {
        const videoId = url.pathname.split('/')[2];
        return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
      }

      if (url.pathname.startsWith('/embed/')) {
        return sanitizedUrl;
      }
    }

    if (host === 'youtube-nocookie.com' && url.pathname.startsWith('/embed/')) {
      return sanitizedUrl;
    }

    if (host === 'vimeo.com') {
      const videoId = url.pathname.split('/').filter(Boolean)[0];
      return videoId ? `https://player.vimeo.com/video/${videoId}` : '';
    }

    if (host === 'player.vimeo.com' && url.pathname.startsWith('/video/')) {
      return sanitizedUrl;
    }

    return '';
  } catch {
    return '';
  }
}

async function resizeImageForBlog(file) {
  const image = await new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('画像を読み込めませんでした。'));
    };
    img.src = objectUrl;
  });

  const maxWidth = 1600;
  const scale = Math.min(1, maxWidth / image.naturalWidth);
  const width = Math.round(image.naturalWidth * scale);
  const height = Math.round(image.naturalHeight * scale);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  context.drawImage(image, 0, 0, width, height);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('画像を圧縮できませんでした。'));
          return;
        }

        resolve(blob);
      },
      'image/webp',
      0.82
    );
  });
}

async function cropThumbnailForBlog(file, crop) {
  const image = await new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('サムネイル画像を読み込めませんでした。'));
    };
    img.src = objectUrl;
  });

  const targetRatio = THUMBNAIL_WIDTH / THUMBNAIL_HEIGHT;
  const imageRatio = image.naturalWidth / image.naturalHeight;
  let baseWidth = image.naturalWidth;
  let baseHeight = image.naturalHeight;

  if (imageRatio > targetRatio) {
    baseWidth = image.naturalHeight * targetRatio;
  } else {
    baseHeight = image.naturalWidth / targetRatio;
  }

  const zoom = Number(crop.zoom) || 1;
  const sourceWidth = Math.max(1, baseWidth / zoom);
  const sourceHeight = Math.max(1, baseHeight / zoom);
  const maxSourceX = Math.max(0, image.naturalWidth - sourceWidth);
  const maxSourceY = Math.max(0, image.naturalHeight - sourceHeight);
  const sourceX = maxSourceX * ((Number(crop.x) || 50) / 100);
  const sourceY = maxSourceY * ((Number(crop.y) || 50) / 100);

  const canvas = document.createElement('canvas');
  canvas.width = THUMBNAIL_WIDTH;
  canvas.height = THUMBNAIL_HEIGHT;

  const context = canvas.getContext('2d');
  context.drawImage(
    image,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    THUMBNAIL_WIDTH,
    THUMBNAIL_HEIGHT
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('サムネイルを作成できませんでした。'));
          return;
        }

        resolve(blob);
      },
      'image/webp',
      0.86
    );
  });
}

function Blog() {
  const location = useLocation();
  const navigate = useNavigate();
  const { postId } = useParams();
  const isRootPage = location.pathname === '/blog/root';
  const [posts, setPosts] = useState(samplePosts);
  const [session, setSession] = useState(null);
  const [isBlogAdmin, setIsBlogAdmin] = useState(false);
  const [isAdminLoading, setIsAdminLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [draft, setDraft] = useState(emptyDraft);
  const [savedDrafts, setSavedDrafts] = useState(() => readSavedDrafts());
  const [activeDraftId, setActiveDraftId] = useState(null);
  const [editingPostId, setEditingPostId] = useState(null);
  const [adminView, setAdminView] = useState('compose');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [customCategories, setCustomCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(isSupabaseConfigured);
  const [message, setMessage] = useState('');
  const [editorMessage, setEditorMessage] = useState('');
  const [legacyPosts, setLegacyPosts] = useState([]);
  const [mfaLevel, setMfaLevel] = useState(null);
  const [mfaFactors, setMfaFactors] = useState([]);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaEnrollCode, setMfaEnrollCode] = useState('');
  const [mfaEnrollment, setMfaEnrollment] = useState(null);
  const [isMfaLoading, setIsMfaLoading] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [imageUploads, setImageUploads] = useState({});
  const [fileUploads, setFileUploads] = useState({});
  const [imagePreviews, setImagePreviews] = useState({});
  const [thumbnailUpload, setThumbnailUpload] = useState(null);
  const [thumbnailCrop, setThumbnailCrop] = useState({
    x: 50,
    y: 50,
    zoom: 1,
  });
  const isMountedRef = useRef(false);
  const mfaRefreshQueueRef = useRef(Promise.resolve());
  const imagePreviewsRef = useRef({});

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(
    () => () => {
      if (thumbnailUpload?.previewUrl) {
        URL.revokeObjectURL(thumbnailUpload.previewUrl);
      }
    },
    [thumbnailUpload]
  );

  useEffect(() => {
    imagePreviewsRef.current = imagePreviews;
  }, [imagePreviews]);

  useEffect(
    () => () => {
      Object.values(imagePreviewsRef.current).forEach((previewUrl) => {
        URL.revokeObjectURL(previewUrl);
      });
    },
    []
  );

  const loadPosts = useCallback(async () => {
    if (!isSupabaseConfigured) {
      const localPosts = readLegacyPosts();
      setPosts(localPosts.length ? localPosts : samplePosts);
      setLegacyPosts(localPosts);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    let { data, error } = await supabase
      .from('blog_posts')
      .select(
        'id,title,summary,category,categories,thumbnail_url,blocks,created_at'
      )
      .order('created_at', { ascending: false });

    if (error && error.message.includes('categories')) {
      const fallbackResult = await supabase
        .from('blog_posts')
        .select('id,title,summary,category,thumbnail_url,blocks,created_at')
        .order('created_at', { ascending: false });

      data = fallbackResult.data;
      error = fallbackResult.error;
    }

    if (error) {
      setMessage(`記事を読み込めませんでした: ${error.message}`);
      setPosts(samplePosts);
    } else {
      setPosts(data.length ? data.map(mapPostFromSupabase) : samplePosts);
    }

    setLegacyPosts(readLegacyPosts());
    setIsLoading(false);
  }, []);

  const refreshMfaState = useCallback(async () => {
    if (!isSupabaseConfigured) return;

    const runRefresh = async () => {
      if (!isMountedRef.current) return;

      setIsMfaLoading(true);

      try {
        const levelResult =
          await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (!isMountedRef.current) return;

        if (levelResult.error) {
          setMessage(
            `二段階認証の状態を確認できませんでした: ${levelResult.error.message}`
          );
          setMfaLevel(null);
        } else {
          setMfaLevel(levelResult.data);
        }

        const factorsResult = await supabase.auth.mfa.listFactors();
        if (!isMountedRef.current) return;

        if (factorsResult.error) {
          setMessage(
            `二段階認証の登録情報を確認できませんでした: ${factorsResult.error.message}`
          );
          setMfaFactors([]);
        } else {
          setMfaFactors(factorsResult.data.totp || []);
        }
      } catch (error) {
        if (isMountedRef.current && error.name !== 'AbortError') {
          setMessage(
            `二段階認証の確認中にエラーが発生しました: ${error.message}`
          );
        }
      } finally {
        if (isMountedRef.current) {
          setIsMfaLoading(false);
        }
      }
    };

    const queuedRefresh = mfaRefreshQueueRef.current
      .catch(() => {})
      .then(runRefresh);
    mfaRefreshQueueRef.current = queuedRefresh.catch(() => {});

    await queuedRefresh;
  }, []);

  const refreshBlogAdminState = useCallback(async (nextSession) => {
    if (!isSupabaseConfigured || !nextSession?.user?.id) {
      setIsBlogAdmin(false);
      return;
    }

    setIsAdminLoading(true);

    const { data, error } = await supabase
      .from('blog_admins')
      .select('user_id,enabled')
      .eq('user_id', nextSession.user.id)
      .eq('enabled', true)
      .maybeSingle();

    if (error) {
      setIsBlogAdmin(false);
      setMessage(`管理者権限を確認できませんでした: ${error.message}`);
    } else {
      setIsBlogAdmin(Boolean(data));
    }

    setIsAdminLoading(false);
  }, []);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  useEffect(() => {
    if (!isSupabaseConfigured) return undefined;

    let isCancelled = false;

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (isCancelled) return;

      setSession(nextSession);
      setIsBlogAdmin(false);
      setMfaEnrollment(null);
      setMfaCode('');
      setMfaEnrollCode('');

      if (!nextSession) {
        setMfaLevel(null);
        setMfaFactors([]);
      }
    });

    return () => {
      isCancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!session) return undefined;

    const timerId = window.setTimeout(() => {
      refreshBlogAdminState(session);
      refreshMfaState();
    }, 0);

    return () => window.clearTimeout(timerId);
  }, [refreshBlogAdminState, refreshMfaState, session]);

  const activePost = useMemo(() => {
    return postId ? posts.find((post) => post.id === postId) || null : null;
  }, [postId, posts]);

  useEffect(() => {
    if (!postId) return;

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'auto',
    });
  }, [postId]);

  const categoryOptions = useMemo(
    () =>
      Array.from(
        new Set([
          ...customCategories,
          ...posts.flatMap((post) => getPostCategories(post)),
          'Blog',
        ])
      ),
    [customCategories, posts]
  );

  const draftCategories = useMemo(
    () => parseCategories(draft.category),
    [draft.category]
  );

  const verifiedMfaFactors = useMemo(
    () => mfaFactors.filter((factor) => factor.status === 'verified'),
    [mfaFactors]
  );

  const isMfaVerified = mfaLevel?.currentLevel === 'aal2';
  const requiresMfaChallenge =
    session &&
    mfaLevel?.currentLevel === 'aal1' &&
    mfaLevel?.nextLevel === 'aal2';
  const requiresMfaEnrollment =
    session &&
    mfaLevel?.nextLevel === 'aal1' &&
    verifiedMfaFactors.length === 0;
  const canManagePosts = Boolean(session && isBlogAdmin && isMfaVerified);

  const previewPost = useMemo(
    () => ({
      id: 'draft-preview',
      title: draft.title.trim() || 'Untitled',
      summary: draft.summary.trim(),
      category: draftCategories[0] || 'Blog',
      categories: draftCategories.length ? draftCategories : ['Blog'],
      thumbnailUrl:
        thumbnailUpload?.previewUrl ||
        (draft.thumbnailUrl.startsWith('selected:') ? '' : draft.thumbnailUrl),
      createdAt: formatDate(new Date().toISOString()),
      createdAtIso: new Date().toISOString(),
      displayDate: formatDateTime(new Date().toISOString()),
      blocks: normalizeBlocks(draft.blocks),
    }),
    [draft, draftCategories, thumbnailUpload]
  );

  const draftStats = useMemo(() => {
    const normalizedBlocks = normalizeBlocks(draft.blocks);
    const textBlocks = normalizedBlocks.filter(
      (block) => block.type === 'text'
    );
    const headingCount = textBlocks.filter(
      (block) => block.format === 'h2'
    ).length;
    const imageCount = normalizedBlocks.filter(
      (block) => block.type === 'image'
    ).length;
    const linkCount = normalizedBlocks.filter(
      (block) => block.type === 'link'
    ).length;
    const characterCount = textBlocks.reduce(
      (total, block) => total + (block.value?.trim().length || 0),
      0
    );

    return {
      blockCount: normalizedBlocks.length,
      headingCount,
      imageCount,
      linkCount,
      characterCount,
    };
  }, [draft.blocks]);

  useEffect(() => {
    if (!canManagePosts || editingPostId) return;

    const hasDraftContent =
      draft.title ||
      draft.summary ||
      draft.category ||
      draft.thumbnailUrl ||
      draft.includeToc ||
      normalizeBlocks(draft.blocks).length > 0;

    if (!hasDraftContent) return;

    const timerId = window.setTimeout(() => {
      setSavedDrafts((currentDrafts) => {
        const draftId = activeDraftId || crypto.randomUUID();
        const nextRecord = createDraftRecord(draft, draftId);
        const nextDrafts = [
          nextRecord,
          ...currentDrafts.filter((record) => record.id !== draftId),
        ];

        writeSavedDrafts(nextDrafts);
        if (!activeDraftId) {
          setActiveDraftId(draftId);
        }

        return nextDrafts;
      });
    }, 600);

    return () => window.clearTimeout(timerId);
  }, [activeDraftId, canManagePosts, draft, editingPostId]);

  const filteredPosts = useMemo(() => {
    const nextPosts =
      selectedCategories.length === 0
        ? posts
        : posts.filter((post) =>
            getPostCategories(post).some((category) =>
              selectedCategories.includes(category)
            )
          );

    return [...nextPosts].sort(
      (a, b) =>
        new Date(b.createdAtIso || b.createdAt) -
        new Date(a.createdAtIso || a.createdAt)
    );
  }, [posts, selectedCategories]);

  const recommendedPosts = useMemo(() => {
    if (!activePost) return [];

    const activeCategories = getPostCategories(activePost);

    return posts
      .filter((post) => post.id !== activePost.id)
      .sort((a, b) => {
        const aMatches = getPostCategories(a).some((category) =>
          activeCategories.includes(category)
        );
        const bMatches = getPostCategories(b).some((category) =>
          activeCategories.includes(category)
        );

        if (aMatches && !bMatches) {
          return -1;
        }

        if (!aMatches && bMatches) {
          return 1;
        }

        return (
          new Date(b.createdAtIso || b.createdAt) -
          new Date(a.createdAtIso || a.createdAt)
        );
      })
      .slice(0, 4);
  }, [activePost, posts]);

  useEffect(() => {
    const title = activePost
      ? `${activePost.title} | ${SITE_NAME}`
      : isRootPage
        ? `投稿管理 | ${SITE_NAME}`
        : `Blog | ${SITE_NAME}`;
    const description = activePost
      ? getPostDescription(activePost)
      : DEFAULT_DESCRIPTION;
    const canonicalPath = activePost
      ? `/blog/${activePost.id}`
      : isRootPage
        ? '/blog/root'
        : '/blog';
    const canonicalUrl = getAbsoluteUrl(canonicalPath);
    const imageUrl = activePost
      ? getPostImage(activePost)
      : getAbsoluteUrl(DEFAULT_IMAGE_PATH);

    document.title = title;
    upsertMeta('meta[name="description"]', {
      name: 'description',
      content: description,
    });
    upsertMeta('meta[name="robots"]', {
      name: 'robots',
      content: isRootPage ? 'noindex,nofollow' : 'index,follow',
    });
    upsertMeta('meta[property="og:type"]', {
      property: 'og:type',
      content: activePost ? 'article' : 'website',
    });
    upsertMeta('meta[property="og:site_name"]', {
      property: 'og:site_name',
      content: SITE_NAME,
    });
    upsertMeta('meta[property="og:title"]', {
      property: 'og:title',
      content: title,
    });
    upsertMeta('meta[property="og:description"]', {
      property: 'og:description',
      content: description,
    });
    upsertMeta('meta[property="og:url"]', {
      property: 'og:url',
      content: canonicalUrl,
    });
    upsertMeta('meta[property="og:image"]', {
      property: 'og:image',
      content: imageUrl,
    });
    upsertMeta('meta[name="twitter:card"]', {
      name: 'twitter:card',
      content: 'summary_large_image',
    });
    setCanonical(canonicalUrl);

    setStructuredData(
      activePost
        ? {
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: activePost.title,
            description,
            image: imageUrl,
            url: canonicalUrl,
            datePublished: activePost.createdAtIso || activePost.createdAt,
            dateModified: activePost.createdAtIso || activePost.createdAt,
            author: {
              '@type': 'Person',
              name: SITE_NAME,
              url: getSiteOrigin(),
            },
            publisher: {
              '@type': 'Person',
              name: SITE_NAME,
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': canonicalUrl,
            },
          }
        : null
    );
  }, [activePost, isRootPage]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setMessage('');

    if (!isSupabaseConfigured) {
      setMessage('.env に Supabase の URL と anon key を設定してください。');
      return;
    }

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      setMessage(`ログインできませんでした: ${error.message}`);
      return;
    }

    setPassword('');
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  const startMfaEnrollment = async () => {
    setMessage('');
    setIsMfaLoading(true);

    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: 'totp',
      friendlyName: 'pf-site blog admin',
      issuer: 'pf-site',
    });

    setIsMfaLoading(false);

    if (error) {
      setMessage(`二段階認証を開始できませんでした: ${error.message}`);
      return;
    }

    setMfaEnrollment({
      factorId: data.id,
      qr: data.totp.qr_code,
      secret: data.totp.secret,
    });
  };

  const verifyMfaEnrollment = async (event) => {
    event.preventDefault();
    setMessage('');

    if (!mfaEnrollment) {
      setMessage('先に二段階認証の登録を開始してください。');
      return;
    }

    const challenge = await supabase.auth.mfa.challenge({
      factorId: mfaEnrollment.factorId,
    });

    if (challenge.error) {
      setMessage(
        `確認コードを準備できませんでした: ${challenge.error.message}`
      );
      return;
    }

    const verify = await supabase.auth.mfa.verify({
      factorId: mfaEnrollment.factorId,
      challengeId: challenge.data.id,
      code: mfaEnrollCode.trim(),
    });

    if (verify.error) {
      setMessage(`二段階認証を有効化できませんでした: ${verify.error.message}`);
      return;
    }

    setMfaEnrollment(null);
    setMfaEnrollCode('');
    setMessage('二段階認証を有効化しました。');
    await refreshMfaState();
  };

  const verifyMfaChallenge = async (event) => {
    event.preventDefault();
    setMessage('');

    const factor = verifiedMfaFactors[0];
    if (!factor) {
      setMessage('確認済みの二段階認証がありません。登録から始めてください。');
      return;
    }

    const challenge = await supabase.auth.mfa.challenge({
      factorId: factor.id,
    });

    if (challenge.error) {
      setMessage(
        `確認コードを準備できませんでした: ${challenge.error.message}`
      );
      return;
    }

    const verify = await supabase.auth.mfa.verify({
      factorId: factor.id,
      challengeId: challenge.data.id,
      code: mfaCode.trim(),
    });

    if (verify.error) {
      setMessage(`確認コードが正しくありません: ${verify.error.message}`);
      return;
    }

    setMfaCode('');
    setMessage('二段階認証が完了しました。');
    await refreshMfaState();
  };

  const updateBlock = (index, field, value) => {
    setDraft((currentDraft) => ({
      ...currentDraft,
      blocks: currentDraft.blocks.map((block, blockIndex) =>
        blockIndex === index ? { ...block, [field]: value } : block
      ),
    }));
  };

  const updateBlockType = (index, type) => {
    const nextBlock = createBlock(type);

    setDraft((currentDraft) => ({
      ...currentDraft,
      blocks: currentDraft.blocks.map((block, blockIndex) =>
        blockIndex === index ? nextBlock : block
      ),
    }));
  };

  const addBlock = (type) => {
    setDraft((currentDraft) => ({
      ...currentDraft,
      blocks: [...currentDraft.blocks, createBlock(type)],
    }));
  };

  const moveBlock = (index, direction) => {
    setDraft((currentDraft) => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= currentDraft.blocks.length) {
        return currentDraft;
      }

      const nextBlocks = [...currentDraft.blocks];
      const [targetBlock] = nextBlocks.splice(index, 1);
      nextBlocks.splice(nextIndex, 0, targetBlock);

      return {
        ...currentDraft,
        blocks: nextBlocks,
      };
    });
  };

  const removeBlock = (index) => {
    setDraft((currentDraft) => ({
      ...currentDraft,
      blocks: currentDraft.blocks.filter(
        (_block, blockIndex) => blockIndex !== index
      ),
    }));
  };

  const handleImageFile = (index, file) => {
    if (!file) return;

    const uploadKey = createUploadToken(file);

    setImageUploads((currentUploads) => ({
      ...currentUploads,
      [uploadKey]: file,
    }));
    setImagePreviews((currentPreviews) => {
      const nextPreviews = { ...currentPreviews };
      nextPreviews[uploadKey] = URL.createObjectURL(file);
      return nextPreviews;
    });

    updateBlock(index, 'value', `selected:${uploadKey}`);
  };

  const handleFileAttachment = (index, file) => {
    if (!file) return;

    const extension = getFileExtension(file);
    if (BLOCKED_ATTACHMENT_EXTENSIONS.has(extension)) {
      setEditorMessage(
        `この種類のファイルはアップロードできません: .${extension}`
      );
      return;
    }

    const uploadKey = createUploadToken(file);

    setFileUploads((currentUploads) => ({
      ...currentUploads,
      [uploadKey]: file,
    }));
    setDraft((currentDraft) => ({
      ...currentDraft,
      blocks: currentDraft.blocks.map((block, blockIndex) =>
        blockIndex === index
          ? {
              ...block,
              url: `selected:${uploadKey}`,
              label: block.label || file.name,
              fileName: file.name,
              fileSize: file.size,
              mimeType: file.type || '',
            }
          : block
      ),
    }));
  };

  const handleThumbnailFile = (file) => {
    if (!file) return;

    setThumbnailUpload((currentUpload) => {
      if (currentUpload?.previewUrl) {
        URL.revokeObjectURL(currentUpload.previewUrl);
      }

      return {
        file,
        previewUrl: URL.createObjectURL(file),
      };
    });
    setThumbnailCrop({ x: 50, y: 50, zoom: 1 });
    setDraft((currentDraft) => ({
      ...currentDraft,
      thumbnailUrl: `selected:${file.name}`,
    }));
  };

  const toggleSelectedCategory = (category) => {
    setSelectedCategories((currentCategories) =>
      currentCategories.includes(category)
        ? currentCategories.filter(
            (currentCategory) => currentCategory !== category
          )
        : [...currentCategories, category]
    );
  };

  const addCategory = () => {
    const category = newCategoryName.trim();
    if (!category) return;

    setCustomCategories((currentCategories) =>
      currentCategories.includes(category)
        ? currentCategories
        : [...currentCategories, category]
    );
    setDraft((currentDraft) => ({
      ...currentDraft,
      category: serializeCategories([...draftCategories, category]),
    }));
    setNewCategoryName('');
  };

  const toggleDraftCategory = (category) => {
    setDraft((currentDraft) => {
      const currentCategories = parseCategories(currentDraft.category);
      const nextCategories = currentCategories.includes(category)
        ? currentCategories.filter(
            (currentCategory) => currentCategory !== category
          )
        : [...currentCategories, category];

      return {
        ...currentDraft,
        category: serializeCategories(nextCategories),
      };
    });
  };

  const startNewDraft = () => {
    setDraft(emptyDraft);
    setActiveDraftId(null);
    setEditingPostId(null);
    setImageUploads({});
    setFileUploads({});
    setImagePreviews({});
    setThumbnailUpload(null);
    setThumbnailCrop({ x: 50, y: 50, zoom: 1 });
    setAdminView('compose');
  };

  const saveDraftNow = () => {
    const draftId = activeDraftId || crypto.randomUUID();

    setSavedDrafts((currentDrafts) => {
      const nextDrafts = [
        createDraftRecord(draft, draftId),
        ...currentDrafts.filter((record) => record.id !== draftId),
      ];
      writeSavedDrafts(nextDrafts);
      return nextDrafts;
    });
    setActiveDraftId(draftId);
    setEditorMessage('下書きを保存しました。');
  };

  const loadSavedDraft = (draftRecord) => {
    setDraft({
      ...emptyDraft,
      ...draftRecord.draft,
      blocks: draftRecord.draft.blocks?.length
        ? draftRecord.draft.blocks
        : emptyDraft.blocks,
    });
    setActiveDraftId(draftRecord.id);
    setEditingPostId(null);
    setImageUploads({});
    setFileUploads({});
    setImagePreviews({});
    setThumbnailUpload(null);
    setThumbnailCrop({ x: 50, y: 50, zoom: 1 });
    setAdminView('compose');
  };

  const deleteSavedDraft = (draftId) => {
    setSavedDrafts((currentDrafts) => {
      const nextDrafts = currentDrafts.filter(
        (record) => record.id !== draftId
      );
      writeSavedDrafts(nextDrafts);
      return nextDrafts;
    });

    if (activeDraftId === draftId) {
      setActiveDraftId(null);
    }
  };

  const startEditingPost = (post) => {
    setDraft(mapDraftFromPost(post));
    setEditingPostId(post.id);
    setActiveDraftId(null);
    setImageUploads({});
    setFileUploads({});
    setImagePreviews({});
    setThumbnailUpload(null);
    setThumbnailCrop({ x: 50, y: 50, zoom: 1 });
    setAdminView('compose');
  };

  const uploadThumbnailImage = async () => {
    if (!thumbnailUpload?.file) {
      return draft.thumbnailUrl.trim();
    }

    const uploadFile = await cropThumbnailForBlog(
      thumbnailUpload.file,
      thumbnailCrop
    );
    const filePath = `${session.user.id}/thumbnails/${Date.now()}.webp`;

    setEditorMessage(
      `サムネイルをアップロードしています: ${thumbnailUpload.file.name}`
    );

    const { error } = await supabase.storage
      .from('blog-images')
      .upload(filePath, uploadFile, {
        cacheControl: '31536000',
        contentType: 'image/webp',
        upsert: false,
      });

    if (error) {
      throw new Error(
        `サムネイルをアップロードできませんでした: ${error.message}`
      );
    }

    const {
      data: { publicUrl },
    } = supabase.storage.from('blog-images').getPublicUrl(filePath);

    return publicUrl;
  };

  const uploadDraftAssets = async (blocks) => {
    const nextBlocks = [...blocks];

    for (let index = 0; index < nextBlocks.length; index += 1) {
      const block = nextBlocks[index];

      if (block.type === 'image') {
        const uploadKey = getSelectedUploadKey(block.value);
        const file = uploadKey ? imageUploads[uploadKey] : null;
        if (!file) continue;

        const uploadFile = await resizeImageForBlog(file);
        const fallbackExtension = getFileExtension(file);
        const extension =
          uploadFile.type === 'image/webp' ? 'webp' : fallbackExtension;
        const filePath = `${session.user.id}/images/${Date.now()}-${index}.${extension}`;

        setEditorMessage(`画像をアップロードしています: ${file.name}`);

        const { error } = await supabase.storage
          .from('blog-images')
          .upload(filePath, uploadFile, {
            cacheControl: '31536000',
            contentType: uploadFile.type || file.type,
            upsert: false,
          });

        if (error) {
          throw new Error(
            `画像をアップロードできませんでした: ${error.message}`
          );
        }

        const {
          data: { publicUrl },
        } = supabase.storage.from('blog-images').getPublicUrl(filePath);

        nextBlocks[index] = {
          ...block,
          value: publicUrl,
        };
      }

      if (block.type === 'file') {
        const uploadKey = getSelectedUploadKey(block.url);
        const file = uploadKey ? fileUploads[uploadKey] : null;
        if (!file) continue;

        const extension = getFileExtension(file);
        const filePath = `${session.user.id}/attachments/${Date.now()}-${index}.${extension}`;

        setEditorMessage(`ファイルをアップロードしています: ${file.name}`);

        const { error } = await supabase.storage
          .from('blog-images')
          .upload(filePath, file, {
            cacheControl: '31536000',
            contentType: file.type || 'application/octet-stream',
            upsert: false,
          });

        if (error) {
          throw new Error(
            `ファイルをアップロードできませんでした: ${error.message}`
          );
        }

        const {
          data: { publicUrl },
        } = supabase.storage.from('blog-images').getPublicUrl(filePath);

        nextBlocks[index] = {
          ...block,
          url: publicUrl,
          label: block.label || file.name,
          fileName: block.fileName || file.name,
          fileSize: block.fileSize || file.size,
          mimeType: block.mimeType || file.type || '',
        };
      }
    }

    return nextBlocks;
  };

  const publishPost = async (event) => {
    event.preventDefault();
    setMessage('');
    setEditorMessage('');

    const trimmedTitle = draft.title.trim();
    if (!trimmedTitle) {
      const nextMessage = 'タイトルを入力してください。';
      setMessage(nextMessage);
      setEditorMessage(nextMessage);
      return;
    }

    if (!canManagePosts) {
      const nextMessage = '投稿するには二段階認証を完了してください。';
      setMessage(nextMessage);
      setEditorMessage(nextMessage);
      return;
    }

    setIsPublishing(true);
    setEditorMessage('投稿しています。');

    try {
      const uploadedBlocks = await uploadDraftAssets(draft.blocks);
      const blocksForSave = prepareBlocksForSave({
        ...draft,
        blocks: uploadedBlocks,
      });
      const uploadedThumbnailUrl = await uploadThumbnailImage();
      const categories = parseCategories(draft.category);
      const postPayload = {
        title: trimmedTitle,
        summary: draft.summary.trim(),
        category: categories[0] || 'Blog',
        categories: categories.length ? categories : ['Blog'],
        thumbnail_url:
          uploadedThumbnailUrl ||
          uploadedBlocks.find((block) => block.type === 'image' && block.value)
            ?.value ||
          null,
        blocks: blocksForSave,
        owner_id: session.user.id,
      };

      const query = editingPostId
        ? supabase
            .from('blog_posts')
            .update(postPayload)
            .eq('id', editingPostId)
            .select(
              'id,title,summary,category,categories,thumbnail_url,blocks,created_at'
            )
            .single()
        : supabase
            .from('blog_posts')
            .insert(postPayload)
            .select(
              'id,title,summary,category,categories,thumbnail_url,blocks,created_at'
            )
            .single();

      const { data, error } = await query;

      if (error) {
        const nextMessage = `投稿できませんでした: ${error.message}`;
        setMessage(nextMessage);
        setEditorMessage(nextMessage);
        return;
      }

      const nextPost = mapPostFromSupabase(data);
      setPosts((currentPosts) => [
        nextPost,
        ...currentPosts.filter(
          (post) => post.id !== samplePosts[0].id && post.id !== nextPost.id
        ),
      ]);
      setDraft(emptyDraft);
      if (activeDraftId) {
        deleteSavedDraft(activeDraftId);
      }
      setActiveDraftId(null);
      setEditingPostId(null);
      setImageUploads({});
      setFileUploads({});
      setImagePreviews({});
      setThumbnailUpload(null);
      setThumbnailCrop({ x: 50, y: 50, zoom: 1 });
      setEditorMessage('投稿しました。');
      navigate(`/blog/${nextPost.id}`);
    } catch (error) {
      const nextMessage = `投稿中にエラーが発生しました: ${error.message}`;
      setMessage(nextMessage);
      setEditorMessage(nextMessage);
    } finally {
      setIsPublishing(false);
    }
  };

  const deletePost = async (postId) => {
    setMessage('');

    if (!canManagePosts) {
      setMessage('削除するには二段階認証を完了してください。');
      return;
    }

    const { error } = await supabase
      .from('blog_posts')
      .delete()
      .eq('id', postId);

    if (error) {
      setMessage(`削除できませんでした: ${error.message}`);
      return;
    }

    const nextPosts = posts.filter((post) => post.id !== postId);
    setPosts(nextPosts.length ? nextPosts : samplePosts);
  };

  const migrateLegacyPosts = async () => {
    setMessage('');

    if (!canManagePosts) {
      setMessage('移行するには二段階認証を完了してください。');
      return;
    }

    const inserts = legacyPosts.map((post) => ({
      title: post.title,
      summary: post.summary || '',
      category: post.category || 'Blog',
      categories: getPostCategories(post),
      thumbnail_url:
        post.thumbnailUrl ||
        post.blocks?.find((block) => block.type === 'image' && block.value)
          ?.value ||
        null,
      blocks: normalizeBlocks(post.blocks),
      owner_id: session.user.id,
      created_at: post.createdAt
        ? new Date(post.createdAt).toISOString()
        : new Date().toISOString(),
    }));

    const { error } = await supabase.from('blog_posts').insert(inserts);

    if (error) {
      setMessage(`移行できませんでした: ${error.message}`);
      return;
    }

    window.localStorage.removeItem(LEGACY_STORAGE_KEY);
    setLegacyPosts([]);
    setMessage('localStorage の記事を Supabase に移行しました。');
    await loadPosts();
  };

  const renderMfaGate = () => {
    if (!session) return null;

    if (isAdminLoading) {
      return <p className="blog-status">管理者権限を確認しています。</p>;
    }

    if (!isBlogAdmin) {
      return (
        <p className="blog-status">
          このアカウントにはブログ管理権限がありません。
        </p>
      );
    }

    if (isMfaLoading || !mfaLevel) {
      return <p className="blog-status">二段階認証の状態を確認しています。</p>;
    }

    if (isMfaVerified) return null;

    if (requiresMfaChallenge) {
      return (
        <form className="blog-login" onSubmit={verifyMfaChallenge}>
          <h3>二段階認証</h3>
          <p>認証アプリに表示されている6桁のコードを入力してください。</p>
          <label htmlFor="mfa-code">Authenticator code</label>
          <input
            id="mfa-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={mfaCode}
            onChange={(event) => setMfaCode(event.target.value)}
            placeholder="123456"
          />
          <button type="submit">Verify</button>
        </form>
      );
    }

    if (requiresMfaEnrollment) {
      return (
        <section className="blog-login">
          <h3>二段階認証を有効化</h3>
          <p>
            投稿管理を使う前に、Google Authenticator、1Password、Authy
            などの認証アプリで二段階認証を登録してください。
          </p>

          {!mfaEnrollment ? (
            <button type="button" onClick={startMfaEnrollment}>
              Setup 2FA
            </button>
          ) : (
            <form className="blog-mfa-form" onSubmit={verifyMfaEnrollment}>
              <img
                className="blog-mfa-qr"
                src={mfaEnrollment.qr}
                alt="二段階認証登録用QRコード"
              />
              <p>
                QRコードを読み取れない場合は、このキーを認証アプリに入力してください。
              </p>
              <code className="blog-mfa-secret">{mfaEnrollment.secret}</code>
              <label htmlFor="mfa-enroll-code">Authenticator code</label>
              <input
                id="mfa-enroll-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={mfaEnrollCode}
                onChange={(event) => setMfaEnrollCode(event.target.value)}
                placeholder="123456"
              />
              <button type="submit">Enable 2FA</button>
            </form>
          )}
        </section>
      );
    }

    return (
      <p className="blog-status">
        二段階認証が必要です。ログアウトしてから再度ログインしてください。
      </p>
    );
  };

  const renderPostBody = (post, options = {}) => (
    <div className="blog-reader-body">
      {post.blocks.map((block, index) => {
        if (block.type === 'toc') {
          const tocItems = getTocItems(post.blocks);

          if (tocItems.length === 0) return null;

          return (
            <nav className="blog-toc" key={`${block.type}-${index}`}>
              <h2>目次</h2>
              {tocItems.map((item) => (
                <a key={item.id} href={`#${item.id}`}>
                  {item.label}
                </a>
              ))}
            </nav>
          );
        }

        if (block.type === 'image') {
          const imageSource = getBlockImageSource(block, options.imagePreviews);

          if (!imageSource) return null;

          return (
            <img
              key={`${block.type}-${index}`}
              src={imageSource}
              alt=""
              className="blog-reader-image"
            />
          );
        }

        if (block.type === 'link') {
          const safeLinkUrl = sanitizeLinkTarget(block.url);
          if (!safeLinkUrl) return null;

          if (block.displayStyle === 'card') {
            const safeCardImage = sanitizeImageSource(block.image);

            return (
              <a
                key={`${block.type}-${index}`}
                className="blog-url-card"
                href={safeLinkUrl}
                target="_blank"
                rel="noopener noreferrer nofollow"
              >
                {safeCardImage && <img src={safeCardImage} alt="" />}
                <span>{block.label || safeLinkUrl}</span>
                {block.description && <p>{block.description}</p>}
                <small>{safeLinkUrl}</small>
              </a>
            );
          }

          return (
            <a
              key={`${block.type}-${index}`}
              className="blog-reader-link"
              href={safeLinkUrl}
              target="_blank"
              rel="noopener noreferrer nofollow"
            >
              {block.label || safeLinkUrl}
            </a>
          );
        }

        if (block.type === 'embed') {
          const embedUrl = getEmbedUrl(block.url);
          if (!embedUrl) return null;

          return (
            <figure className="blog-embed-card" key={`${block.type}-${index}`}>
              <div className="blog-embed-frame">
                <iframe
                  src={embedUrl}
                  title={block.title || block.url}
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
              {(block.title || block.caption) && (
                <figcaption>
                  {block.title && <strong>{block.title}</strong>}
                  {block.caption && <span>{block.caption}</span>}
                </figcaption>
              )}
            </figure>
          );
        }

        if (block.type === 'file') {
          const pendingUpload = getSelectedUploadKey(block.url);
          const safeFileUrl = sanitizeFileTarget(block.url);
          const fileLabel = block.label || block.fileName || '添付ファイル';
          const metadata = [block.fileName, formatFileSize(block.fileSize)]
            .filter(Boolean)
            .join(' / ');

          if (pendingUpload) {
            return (
              <div
                className="blog-file-card is-pending"
                key={`${block.type}-${index}`}
              >
                <strong>{fileLabel}</strong>
                {metadata && <span>{metadata}</span>}
                <small>公開時にアップロードされます</small>
              </div>
            );
          }

          if (!safeFileUrl) return null;

          return (
            <a
              className="blog-file-card"
              href={safeFileUrl}
              key={`${block.type}-${index}`}
              target="_blank"
              rel="noopener noreferrer nofollow"
            >
              <strong>{fileLabel}</strong>
              {metadata && <span>{metadata}</span>}
              <small>{safeFileUrl}</small>
            </a>
          );
        }

        if (block.type === 'quote') {
          return (
            <blockquote
              className="blog-reader-quote"
              key={`${block.type}-${index}`}
            >
              <p>{block.value}</p>
              {block.cite && <cite>{block.cite}</cite>}
            </blockquote>
          );
        }

        if (block.type === 'code') {
          return (
            <figure className="blog-reader-code" key={`${block.type}-${index}`}>
              {block.caption && <figcaption>{block.caption}</figcaption>}
              <pre>
                <code data-language={block.language || ''}>{block.value}</code>
              </pre>
            </figure>
          );
        }

        if (block.format === 'h2') {
          const headingIndex = getTocItems(post.blocks).findIndex(
            (item) => item.label === block.value
          );

          return (
            <h2
              id={`toc-heading-${Math.max(headingIndex, 0)}`}
              key={`${block.type}-${index}`}
            >
              {block.value}
            </h2>
          );
        }

        if (block.format === 'bold') {
          return (
            <p key={`${block.type}-${index}`}>
              <strong>{block.value}</strong>
            </p>
          );
        }

        return (
          <p
            className={`blog-text-${block.format || 'paragraph'}`}
            key={`${block.type}-${index}`}
            style={{
              fontSize: block.fontSize ? `${block.fontSize}px` : undefined,
            }}
          >
            {block.value}
          </p>
        );
      })}
    </div>
  );

  if (isRootPage) {
    return (
      <>
        <Header
          eyebrow="Blog"
          title="投稿管理"
          hiddenNavLabels={['Works', 'Avatar', 'Mail']}
        />
        <main className="blog-admin-page">
          <section className="blog-admin-intro">
            <p className="blog-eyebrow">ROOT</p>
            <h2>投稿管理</h2>
            <p>
              Supabase Auth
              と二段階認証でログインし、テキスト、画像、リンクをブロックとして追加して記事を作成できます。
            </p>
          </section>

          {message && <p className="blog-status">{message}</p>}

          {!isSupabaseConfigured && (
            <p className="blog-status">
              Supabase が未設定です。REACT_APP_SUPABASE_URL と
              REACT_APP_SUPABASE_ANON_KEY を .env に追加してください。
            </p>
          )}

          {!session ? (
            <form className="blog-login" onSubmit={handleLogin}>
              <label htmlFor="root-email">Email</label>
              <input
                id="root-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@example.com"
              />
              <label htmlFor="root-password">Password</label>
              <input
                id="root-password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="password"
              />
              <button type="submit">Login</button>
            </form>
          ) : (
            <>
              <div className="blog-editor-actions blog-admin-session-actions">
                <button type="button" onClick={handleLogout}>
                  Logout
                </button>
              </div>

              {renderMfaGate()}

              {canManagePosts && (
                <section className="blog-editor">
                  <div className="blog-admin-tabs" role="tablist">
                    <button
                      type="button"
                      className={adminView === 'manage' ? 'is-active' : ''}
                      onClick={() => setAdminView('manage')}
                    >
                      Blog Manage
                    </button>
                    <button
                      type="button"
                      className={adminView === 'compose' ? 'is-active' : ''}
                      onClick={() => setAdminView('compose')}
                    >
                      Compose
                    </button>
                  </div>

                  <div className="blog-editor-actions">
                    <button type="button" onClick={startNewDraft}>
                      New Draft
                    </button>
                    {adminView === 'compose' && (
                      <button type="button" onClick={saveDraftNow}>
                        Save Draft
                      </button>
                    )}
                    {legacyPosts.length > 0 && (
                      <button type="button" onClick={migrateLegacyPosts}>
                        localStorage の記事を移行
                      </button>
                    )}
                  </div>

                  {adminView === 'compose' && (
                    <div className="blog-editor-workspace">
                      <form
                        className="blog-compose-form"
                        onSubmit={publishPost}
                      >
                        <section className="blog-compose-header">
                          <div>
                            <p className="blog-eyebrow">エディタ</p>
                            <h3>
                              {editingPostId
                                ? '記事を編集'
                                : '新しい記事を作成'}
                            </h3>
                            <p className="blog-compose-copy">
                              記事の基本情報、本文ブロック、プレビューを見比べながら
                              そのまま執筆できます。
                            </p>
                          </div>
                          <div className="blog-editor-metrics">
                            <article>
                              <strong>{draftStats.characterCount}</strong>
                              <span>文字数</span>
                            </article>
                            <article>
                              <strong>{draftStats.blockCount}</strong>
                              <span>ブロック数</span>
                            </article>
                            <article>
                              <strong>{draftStats.headingCount}</strong>
                              <span>見出し数</span>
                            </article>
                            <article>
                              <strong>
                                {draftStats.imageCount + draftStats.linkCount}
                              </strong>
                              <span>画像・リンク</span>
                            </article>
                          </div>
                        </section>
                        <section className="blog-editor-section">
                          <div className="blog-section-heading">
                            <div>
                              <p className="blog-section-kicker">STEP 1</p>
                              <h4>記事の基本情報</h4>
                            </div>
                            <p>
                              まずはタイトル、概要、カテゴリ、サムネイルを整えて
                              記事の土台を作ります。
                            </p>
                          </div>
                          <div className="blog-editor-grid">
                            <label>
                              タイトル
                              <input
                                value={draft.title}
                                onChange={(event) =>
                                  setDraft({
                                    ...draft,
                                    title: event.target.value,
                                  })
                                }
                                placeholder="記事タイトル"
                              />
                            </label>
                            <div className="blog-category-manager">
                              <span>カテゴリ</span>
                              <div className="blog-category-create">
                                <input
                                  value={newCategoryName}
                                  onChange={(event) =>
                                    setNewCategoryName(event.target.value)
                                  }
                                  placeholder="新しいカテゴリを追加"
                                />
                                <button type="button" onClick={addCategory}>
                                  追加
                                </button>
                              </div>
                              <div className="blog-category-filter-list">
                                {categoryOptions.map((category) => (
                                  <label key={category}>
                                    <input
                                      type="checkbox"
                                      checked={draftCategories.includes(
                                        category
                                      )}
                                      onChange={() =>
                                        toggleDraftCategory(category)
                                      }
                                    />
                                    {category}
                                  </label>
                                ))}
                              </div>
                            </div>
                            <label>
                              サムネイル画像
                              <input
                                value={draft.thumbnailUrl}
                                onChange={(event) =>
                                  setDraft({
                                    ...draft,
                                    thumbnailUrl: event.target.value,
                                  })
                                }
                                placeholder="https://example.com/thumbnail.jpg"
                              />
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(event) =>
                                  handleThumbnailFile(event.target.files[0])
                                }
                              />
                            </label>
                          </div>
                        </section>
                        {thumbnailUpload && (
                          <section className="blog-thumbnail-cropper">
                            <div className="blog-thumbnail-preview">
                              <img
                                src={thumbnailUpload.previewUrl}
                                alt="サムネイルプレビュー"
                                style={{
                                  objectPosition: `${thumbnailCrop.x}% ${thumbnailCrop.y}%`,
                                  transform: `scale(${thumbnailCrop.zoom})`,
                                }}
                              />
                            </div>
                            <div className="blog-crop-controls">
                              <label>
                                横位置
                                <input
                                  type="range"
                                  min="0"
                                  max="100"
                                  value={thumbnailCrop.x}
                                  onChange={(event) =>
                                    setThumbnailCrop((currentCrop) => ({
                                      ...currentCrop,
                                      x: Number(event.target.value),
                                    }))
                                  }
                                />
                              </label>
                              <label>
                                縦位置
                                <input
                                  type="range"
                                  min="0"
                                  max="100"
                                  value={thumbnailCrop.y}
                                  onChange={(event) =>
                                    setThumbnailCrop((currentCrop) => ({
                                      ...currentCrop,
                                      y: Number(event.target.value),
                                    }))
                                  }
                                />
                              </label>
                              <label>
                                拡大
                                <input
                                  type="range"
                                  min="1"
                                  max="3"
                                  step="0.05"
                                  value={thumbnailCrop.zoom}
                                  onChange={(event) =>
                                    setThumbnailCrop((currentCrop) => ({
                                      ...currentCrop,
                                      zoom: Number(event.target.value),
                                    }))
                                  }
                                />
                              </label>
                            </div>
                          </section>
                        )}
                        <label>
                          概要
                          <textarea
                            value={draft.summary}
                            onChange={(event) =>
                              setDraft({
                                ...draft,
                                summary: event.target.value,
                              })
                            }
                            placeholder="記事の概要"
                            rows="3"
                          />
                        </label>
                        <label className="blog-inline-check">
                          <input
                            type="checkbox"
                            checked={draft.includeToc}
                            onChange={(event) =>
                              setDraft({
                                ...draft,
                                includeToc: event.target.checked,
                              })
                            }
                          />
                          記事の最初に目次を追加する
                        </label>

                        <section className="blog-editor-section">
                          <div className="blog-section-heading">
                            <div>
                              <p className="blog-section-kicker">Step 2</p>
                              <h4>本文エディタ</h4>
                            </div>
                            <p>
                              ブロックを追加しながら、記事の流れに合わせて順番を
                              入れ替えられます。
                            </p>
                          </div>
                          <div className="blog-block-inserter">
                            {BLOCK_TYPE_OPTIONS.map((option) => (
                              <button
                                key={option.type}
                                type="button"
                                className="blog-block-insert-card"
                                onClick={() => addBlock(option.type)}
                              >
                                <strong>{option.label}</strong>
                                <span>{option.description}</span>
                              </button>
                            ))}
                          </div>
                          <div className="blog-blocks">
                            {draft.blocks.map((block, index) => (
                              <div
                                className="blog-block-editor"
                                key={`${block.type}-${index}`}
                              >
                                <div className="blog-block-editor-header">
                                  <div>
                                    <span className="blog-block-index">
                                      ブロック {index + 1}
                                    </span>
                                    <h5>{getBlockTitle(block)}</h5>
                                  </div>
                                  <div className="blog-block-toolbar">
                                    <select
                                      value={block.type}
                                      onChange={(event) =>
                                        updateBlockType(
                                          index,
                                          event.target.value
                                        )
                                      }
                                    >
                                      {BLOCK_TYPE_OPTIONS.map((option) => (
                                        <option
                                          key={option.type}
                                          value={option.type}
                                        >
                                          {option.label}
                                        </option>
                                      ))}
                                    </select>
                                    <button
                                      type="button"
                                      onClick={() => moveBlock(index, -1)}
                                      disabled={index === 0}
                                    >
                                      上へ
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => moveBlock(index, 1)}
                                      disabled={
                                        index === draft.blocks.length - 1
                                      }
                                    >
                                      下へ
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => removeBlock(index)}
                                    >
                                      削除
                                    </button>
                                  </div>
                                </div>

                                {block.type === 'text' && (
                                  <div className="blog-text-inputs">
                                    <select
                                      value={block.format || 'paragraph'}
                                      onChange={(event) =>
                                        updateBlock(
                                          index,
                                          'format',
                                          event.target.value
                                        )
                                      }
                                    >
                                      {TEXT_FORMAT_OPTIONS.map((option) => (
                                        <option
                                          key={option.value}
                                          value={option.value}
                                        >
                                          {option.label}
                                        </option>
                                      ))}
                                    </select>
                                    <input
                                      type="number"
                                      min="10"
                                      max="48"
                                      value={block.fontSize || ''}
                                      onChange={(event) =>
                                        updateBlock(
                                          index,
                                          'fontSize',
                                          event.target.value
                                        )
                                      }
                                      placeholder="文字サイズ"
                                    />
                                    <textarea
                                      value={block.value}
                                      onChange={(event) =>
                                        updateBlock(
                                          index,
                                          'value',
                                          event.target.value
                                        )
                                      }
                                      placeholder="本文"
                                      rows="5"
                                    />
                                  </div>
                                )}

                                {block.type === 'image' && (
                                  <div className="blog-image-inputs">
                                    <input
                                      value={block.value}
                                      onChange={(event) =>
                                        updateBlock(
                                          index,
                                          'value',
                                          event.target.value
                                        )
                                      }
                                      placeholder="画像URL"
                                    />
                                    <input
                                      type="file"
                                      accept="image/*"
                                      onChange={(event) =>
                                        handleImageFile(
                                          index,
                                          event.target.files[0]
                                        )
                                      }
                                    />
                                  </div>
                                )}

                                {block.type === 'link' && (
                                  <div className="blog-link-inputs">
                                    <select
                                      value={block.displayStyle || 'text'}
                                      onChange={(event) =>
                                        updateBlock(
                                          index,
                                          'displayStyle',
                                          event.target.value
                                        )
                                      }
                                    >
                                      <option value="text">
                                        テキストリンク
                                      </option>
                                      <option value="card">カードリンク</option>
                                    </select>
                                    <input
                                      value={block.label}
                                      onChange={(event) =>
                                        updateBlock(
                                          index,
                                          'label',
                                          event.target.value
                                        )
                                      }
                                      placeholder="リンクテキスト"
                                    />
                                    <input
                                      value={block.url}
                                      onChange={(event) =>
                                        updateBlock(
                                          index,
                                          'url',
                                          event.target.value
                                        )
                                      }
                                      placeholder="https://example.com"
                                    />
                                    {block.displayStyle === 'card' && (
                                      <>
                                        <input
                                          value={block.description || ''}
                                          onChange={(event) =>
                                            updateBlock(
                                              index,
                                              'description',
                                              event.target.value
                                            )
                                          }
                                          placeholder="カード説明"
                                        />
                                        <input
                                          value={block.image || ''}
                                          onChange={(event) =>
                                            updateBlock(
                                              index,
                                              'image',
                                              event.target.value
                                            )
                                          }
                                          placeholder="カード画像URL"
                                        />
                                      </>
                                    )}
                                  </div>
                                )}

                                {block.type === 'embed' && (
                                  <div className="blog-embed-inputs">
                                    <input
                                      value={block.url}
                                      onChange={(event) =>
                                        updateBlock(
                                          index,
                                          'url',
                                          event.target.value
                                        )
                                      }
                                      placeholder="https://www.youtube.com/watch?v=..."
                                    />
                                    <input
                                      value={block.title || ''}
                                      onChange={(event) =>
                                        updateBlock(
                                          index,
                                          'title',
                                          event.target.value
                                        )
                                      }
                                      placeholder="埋め込みタイトル"
                                    />
                                    <textarea
                                      value={block.caption || ''}
                                      onChange={(event) =>
                                        updateBlock(
                                          index,
                                          'caption',
                                          event.target.value
                                        )
                                      }
                                      placeholder="補足説明"
                                      rows="3"
                                    />
                                  </div>
                                )}

                                {block.type === 'file' && (
                                  <div className="blog-file-inputs">
                                    <input
                                      value={block.label || ''}
                                      onChange={(event) =>
                                        updateBlock(
                                          index,
                                          'label',
                                          event.target.value
                                        )
                                      }
                                      placeholder="表示名"
                                    />
                                    <input
                                      value={block.url || ''}
                                      onChange={(event) =>
                                        updateBlock(
                                          index,
                                          'url',
                                          event.target.value
                                        )
                                      }
                                      placeholder="https://example.com/file.pdf"
                                    />
                                    <input
                                      value={block.fileName || ''}
                                      onChange={(event) =>
                                        updateBlock(
                                          index,
                                          'fileName',
                                          event.target.value
                                        )
                                      }
                                      placeholder="ファイル名"
                                    />
                                    <input
                                      type="file"
                                      onChange={(event) =>
                                        handleFileAttachment(
                                          index,
                                          event.target.files[0]
                                        )
                                      }
                                    />
                                  </div>
                                )}

                                {block.type === 'quote' && (
                                  <div className="blog-quote-inputs">
                                    <textarea
                                      value={block.value}
                                      onChange={(event) =>
                                        updateBlock(
                                          index,
                                          'value',
                                          event.target.value
                                        )
                                      }
                                      placeholder="引用文"
                                      rows="4"
                                    />
                                    <input
                                      value={block.cite || ''}
                                      onChange={(event) =>
                                        updateBlock(
                                          index,
                                          'cite',
                                          event.target.value
                                        )
                                      }
                                      placeholder="出典"
                                    />
                                  </div>
                                )}

                                {block.type === 'code' && (
                                  <div className="blog-code-inputs">
                                    <input
                                      value={block.language || ''}
                                      onChange={(event) =>
                                        updateBlock(
                                          index,
                                          'language',
                                          event.target.value
                                        )
                                      }
                                      placeholder="言語: js, ts, css..."
                                    />
                                    <input
                                      value={block.caption || ''}
                                      onChange={(event) =>
                                        updateBlock(
                                          index,
                                          'caption',
                                          event.target.value
                                        )
                                      }
                                      placeholder="コードの補足"
                                    />
                                    <textarea
                                      className="blog-code-textarea"
                                      value={block.value}
                                      onChange={(event) =>
                                        updateBlock(
                                          index,
                                          'value',
                                          event.target.value
                                        )
                                      }
                                      placeholder="console.log('hello world');"
                                      rows="8"
                                    />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>

                          <div className="blog-editor-actions">
                            <button
                              type="button"
                              onClick={() => addBlock('text')}
                            >
                              テキストを追加
                            </button>
                            <button
                              type="button"
                              onClick={() => addBlock('image')}
                            >
                              画像を追加
                            </button>
                            <button
                              type="button"
                              onClick={() => addBlock('link')}
                            >
                              リンクを追加
                            </button>
                            <button
                              type="button"
                              onClick={() => addBlock('embed')}
                            >
                              埋め込みを追加
                            </button>
                            <button
                              type="button"
                              onClick={() => addBlock('file')}
                            >
                              ファイルを追加
                            </button>
                            <button
                              type="button"
                              onClick={() => addBlock('quote')}
                            >
                              引用を追加
                            </button>
                            <button
                              type="button"
                              onClick={() => addBlock('code')}
                            >
                              コードを追加
                            </button>
                            <button
                              type="submit"
                              className="blog-publish-button"
                              disabled={isPublishing}
                            >
                              {isPublishing
                                ? '公開中...'
                                : editingPostId
                                  ? '更新する'
                                  : '公開する'}
                            </button>
                          </div>
                        </section>
                        {editorMessage && (
                          <p className="blog-status blog-editor-status">
                            {editorMessage}
                          </p>
                        )}
                      </form>

                      <aside className="blog-live-preview">
                        <section className="blog-editor-sidecard">
                          <p className="blog-eyebrow">チェックリスト</p>
                          <ul className="blog-editor-checklist">
                            <li
                              className={
                                draft.title.trim() ? 'is-complete' : ''
                              }
                            >
                              タイトルを入力する
                            </li>
                            <li
                              className={
                                draft.summary.trim() ? 'is-complete' : ''
                              }
                            >
                              概要を書く
                            </li>
                            <li
                              className={
                                draftCategories.length > 0 ? 'is-complete' : ''
                              }
                            >
                              カテゴリを1つ以上選ぶ
                            </li>
                            <li
                              className={
                                draftStats.characterCount > 0
                                  ? 'is-complete'
                                  : ''
                              }
                            >
                              本文を追加する
                            </li>
                          </ul>
                        </section>
                        <p className="blog-eyebrow">プレビュー</p>
                        <article className="blog-reader">
                          <div className="blog-reader-head">
                            <div className="blog-category-list">
                              {getPostCategories(previewPost).map(
                                (category) => (
                                  <span key={category}>{category}</span>
                                )
                              )}
                            </div>
                            <h1>{previewPost.title}</h1>
                            <time dateTime={previewPost.createdAtIso}>
                              {previewPost.displayDate}
                            </time>
                            {previewPost.summary && (
                              <p className="blog-preview-summary">
                                {previewPost.summary}
                              </p>
                            )}
                          </div>
                          {previewPost.thumbnailUrl && (
                            <img
                              className="blog-preview-thumbnail"
                              src={previewPost.thumbnailUrl}
                              alt=""
                            />
                          )}
                          {renderPostBody(previewPost, { imagePreviews })}
                        </article>
                      </aside>
                    </div>
                  )}

                  {adminView === 'manage' && (
                    <div className="blog-admin-list">
                      <h3>Drafts</h3>
                      {savedDrafts.length === 0 ? (
                        <p className="blog-status">No drafts</p>
                      ) : (
                        savedDrafts.map((draftRecord) => (
                          <article key={draftRecord.id}>
                            <span>
                              {new Date(draftRecord.updatedAt).toLocaleString(
                                'ja-JP'
                              )}
                            </span>
                            <strong>{getDraftTitle(draftRecord.draft)}</strong>
                            <button
                              type="button"
                              onClick={() => loadSavedDraft(draftRecord)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteSavedDraft(draftRecord.id)}
                            >
                              Delete
                            </button>
                          </article>
                        ))
                      )}

                      <h3>Posts</h3>
                      {posts.map((post) => (
                        <article key={post.id}>
                          <span>{post.createdAt}</span>
                          <strong>{post.title}</strong>
                          <button
                            type="button"
                            onClick={() => startEditingPost(post)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => deletePost(post.id)}
                          >
                            Delete
                          </button>
                        </article>
                      ))}
                    </div>
                  )}
                </section>
              )}
            </>
          )}
        </main>
      </>
    );
  }

  return (
    <>
      <Header
        eyebrow="Blog"
        title="rin0700 notes"
        hiddenNavLabels={['Works', 'Avatar', 'Mail']}
      />
      <main className="blog-page">
        {postId ? (
          <section className="blog-detail-layout">
            {!isLoading && !activePost && (
              <article className="blog-reader">
                <div className="blog-reader-head">
                  <span>Not found</span>
                  <h1>記事が見つかりません</h1>
                  <Link className="blog-reader-permalink" to="/blog">
                    Blogへ戻る
                  </Link>
                </div>
              </article>
            )}

            {activePost && (
              <article className="blog-reader">
                <div className="blog-reader-head">
                  <div className="blog-category-list">
                    {getPostCategories(activePost).map((category) => (
                      <span key={category}>{category}</span>
                    ))}
                  </div>
                  <h1>{activePost.title}</h1>
                  <time
                    dateTime={activePost.createdAtIso || activePost.createdAt}
                  >
                    {activePost.displayDate || activePost.createdAt}
                  </time>
                  <Link className="blog-reader-permalink" to="/blog">
                    Blog一覧へ戻る
                  </Link>
                </div>
                <img
                  className="blog-reader-hero-image"
                  src={getPostThumbnail(activePost)}
                  alt=""
                />
                {renderPostBody(activePost)}
                {recommendedPosts.length > 0 && (
                  <section className="blog-recommendations">
                    <h2>おすすめブログ</h2>
                    <div className="blog-feed">
                      {recommendedPosts.map((post) => (
                        <article className="blog-card" key={post.id}>
                          <Link to={`/blog/${post.id}`}>
                            <img
                              className="blog-card-thumbnail"
                              src={getPostThumbnail(post)}
                              alt=""
                            />
                            <div className="blog-card-content">
                              <h3>{post.title}</h3>
                              <div className="blog-card-meta">
                                <time
                                  dateTime={post.createdAtIso || post.createdAt}
                                >
                                  {post.displayDate || post.createdAt}
                                </time>
                                <span>
                                  {getPostCategories(post).join(', ')}
                                </span>
                              </div>
                            </div>
                          </Link>
                        </article>
                      ))}
                    </div>
                  </section>
                )}
              </article>
            )}
          </section>
        ) : (
          <section className="blog-index">
            <div className="blog-filter">
              <span>カテゴリ</span>
              <button
                type="button"
                onClick={() => setSelectedCategories([])}
                className={selectedCategories.length === 0 ? 'is-active' : ''}
              >
                すべて
              </button>
              <div className="blog-category-filter-list">
                {categoryOptions.map((category) => (
                  <label key={category}>
                    <input
                      type="checkbox"
                      checked={selectedCategories.includes(category)}
                      onChange={() => toggleSelectedCategory(category)}
                    />
                    {category}
                  </label>
                ))}
              </div>
            </div>

            <div className="blog-feed">
              {isLoading ? (
                <p className="blog-status">記事を読み込んでいます。</p>
              ) : filteredPosts.length ? (
                filteredPosts.map((post) => (
                  <article className="blog-card" key={post.id}>
                    <Link to={`/blog/${post.id}`}>
                      <img
                        className="blog-card-thumbnail"
                        src={getPostThumbnail(post)}
                        alt=""
                      />
                      <div className="blog-card-content">
                        <h3>{post.title}</h3>
                        <div className="blog-card-meta">
                          <time dateTime={post.createdAtIso || post.createdAt}>
                            {post.displayDate || post.createdAt}
                          </time>
                          <span>{getPostCategories(post).join(', ')}</span>
                        </div>
                      </div>
                    </Link>
                  </article>
                ))
              ) : (
                <p className="blog-status">該当する記事がありません。</p>
              )}
            </div>
          </section>
        )}
      </main>
    </>
  );
}

export default Blog;
